import React, { useEffect, useState } from "react";
import API from "../services/api";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle, XCircle, Clock, FileText, Download,
  RefreshCw, AlertCircle, Wallet, ArrowRight, Building,
} from "lucide-react";

const formatKES = (amt) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency", currency: "KES", maximumFractionDigits: 0,
  }).format(amt || 0);

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-KE", { dateStyle: "long" }) : "—";

// ── Status badge ──────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  DRAFT:        { label: "Draft",        color: "bg-gray-100 text-gray-600 border-gray-200",    icon: FileText },
  SUBMITTED:    { label: "Submitted",    color: "bg-blue-100 text-blue-700 border-blue-200",    icon: Clock },
  UNDER_REVIEW: { label: "Under Review", color: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: Clock },
  APPROVED:     { label: "Approved",     color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle },
  REJECTED:     { label: "Rejected",     color: "bg-red-100 text-red-700 border-red-200",       icon: XCircle },
};

const DISB_CONFIG = {
  PENDING:   { label: "Pending Payment",  color: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: Clock },
  PROCESSED: { label: "Being Processed",  color: "bg-blue-100 text-blue-700 border-blue-200",    icon: Clock },
  PAID:      { label: "Funds Disbursed",  color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle },
  CANCELLED: { label: "Cancelled",        color: "bg-red-100 text-red-700 border-red-200",       icon: XCircle },
};

const StatusBadge = ({ status, config }) => {
  const cfg = config[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${cfg.color}`}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
};

// ── Timeline step ─────────────────────────────────────────────────────────────
const TimelineStep = ({ label, description, done, active, last }) => (
  <div className="flex gap-3">
    <div className="flex flex-col items-center">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-colors
        ${done   ? "bg-green-500 border-green-500 text-white"
          : active ? "bg-primary border-primary text-white"
          : "bg-white border-gray-200 text-gray-300"}`}
      >
        {done ? <CheckCircle size={14} /> : <span className="text-xs font-bold">{active ? "●" : "○"}</span>}
      </div>
      {!last && <div className={`w-0.5 flex-1 mt-1 ${done ? "bg-green-300" : "bg-gray-200"}`} />}
    </div>
    <div className="pb-6 min-w-0">
      <p className={`text-sm font-semibold ${done || active ? "text-gray-800" : "text-gray-400"}`}>{label}</p>
      <p className={`text-xs mt-0.5 ${done || active ? "text-gray-500" : "text-gray-300"}`}>{description}</p>
    </div>
  </div>
);

// ── Info row ──────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-50 last:border-0">
    <span className="text-sm text-gray-400">{label}</span>
    <span className="text-sm font-medium text-gray-800 text-right max-w-[55%]">{value || "—"}</span>
  </div>
);

// ── Main Status Page ──────────────────────────────────────────────────────────
const Status = () => {
  const navigate = useNavigate();

  const [appStatus,   setAppStatus]   = useState("loading"); // loading | found | not_found | error
  const [disbStatus,  setDisbStatus]  = useState("loading"); // loading | found | not_found
  const [application, setApplication] = useState(null);
  const [disbursement,setDisbursement]= useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchAll = async () => {
    setAppStatus("loading");
    setDisbStatus("loading");

    const [appRes, disbRes] = await Promise.allSettled([
      API.get("/applications/latest"),
      API.get("/applications/disbursement/me"),
    ]);

    // Application
    if (appRes.status === "fulfilled") {
      setApplication(appRes.value.data);
      setAppStatus("found");
    } else if (appRes.reason?.response?.status === 404) {
      setAppStatus("not_found");
    } else {
      setAppStatus("error");
    }

    // Disbursement
    if (disbRes.status === "fulfilled") {
      setDisbursement(disbRes.value.data);
      setDisbStatus("found");
    } else {
      setDisbStatus("not_found");
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleDownloadLetter = async () => {
    setDownloading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res  = await fetch(
        `${API.defaults.baseURL}/applications/disbursement/me/letter`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.message || "Letter not yet available.");
        return;
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href  = window.URL.createObjectURL(blob);
      link.download = "BursaryAwardLetter.pdf";
      link.click();
    } catch {
      alert("Failed to download letter. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // ── Derive timeline state ─────────────────────────────────────────────────
  const appStatusVal = application?.status || "";
  const timeline = [
    {
      label:       "Application Submitted",
      description: application?.submittedAt
        ? `Submitted on ${formatDate(application.submittedAt)}`
        : "Not yet submitted",
      done:   ["SUBMITTED", "UNDER_REVIEW", "APPROVED"].includes(appStatusVal),
      active: appStatusVal === "DRAFT",
    },
    {
      label:       "Under Review",
      description: "CDF committee is reviewing your application",
      done:   ["UNDER_REVIEW", "APPROVED"].includes(appStatusVal),
      active: appStatusVal === "SUBMITTED",
    },
    {
      label:       "Decision Made",
      description: appStatusVal === "APPROVED"
        ? "Your application was approved ✓"
        : appStatusVal === "REJECTED"
        ? "Your application was not successful"
        : "Awaiting committee decision",
      done:   ["APPROVED", "REJECTED"].includes(appStatusVal),
      active: appStatusVal === "UNDER_REVIEW",
    },
    {
      label:       "Funds Disbursed",
      description: disbursement?.status === "PAID"
        ? `${formatKES(disbursement.amountAwarded)} paid on ${formatDate(disbursement.paidAt)}`
        : disbursement
        ? "Payment is being processed"
        : "Pending approval decision",
      done:   disbursement?.status === "PAID",
      active: disbursement && disbursement.status !== "PAID",
    },
  ];

  // ── Loading ───────────────────────────────────────────────────────────────
  if (appStatus === "loading") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">Loading your application status...</p>
      </div>
    );
  }

  // ── No application yet ────────────────────────────────────────────────────
  if (appStatus === "not_found") {
    return (
      <div className="max-w-lg mx-auto px-4 py-10 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
          <FileText size={28} className="text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">No Application Found</h2>
        <p className="text-gray-500 text-sm mb-6">
          You have not submitted a bursary application yet. If applications are currently open, you can apply now.
        </p>
        <button
          onClick={() => navigate("/student-dashboard/application")}
          className="bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-800 transition inline-flex items-center gap-2"
        >
          Apply Now <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (appStatus === "error") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-4">
        <AlertCircle size={36} className="text-red-400" />
        <p className="text-gray-600 text-sm">Failed to load your application. Please try again.</p>
        <button onClick={fetchAll} className="bg-primary text-white px-5 py-2 rounded-xl text-sm font-medium">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-primary">Application Status</h2>
          <p className="text-gray-400 text-sm mt-0.5">Track your bursary application progress</p>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary transition"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Status banner */}
      <div className={`flex items-center gap-4 p-4 rounded-2xl border-2
        ${appStatusVal === "APPROVED" ? "bg-green-50 border-green-200"
          : appStatusVal === "REJECTED" ? "bg-red-50 border-red-200"
          : "bg-blue-50 border-blue-100"}`}
      >
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
          ${appStatusVal === "APPROVED" ? "bg-green-100"
            : appStatusVal === "REJECTED" ? "bg-red-100"
            : "bg-blue-100"}`}
        >
          {appStatusVal === "APPROVED" ? <CheckCircle size={24} className="text-green-600" />
            : appStatusVal === "REJECTED" ? <XCircle size={24} className="text-red-500" />
            : <Clock size={24} className="text-blue-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <p className="font-semibold text-gray-800">
              {application?.academicYear} Bursary Application
            </p>
            <StatusBadge status={appStatusVal} config={STATUS_CONFIG} />
          </div>
          <p className="text-xs text-gray-500">
            {appStatusVal === "APPROVED"
              ? "Congratulations! Your application has been approved by the CDF committee."
              : appStatusVal === "REJECTED"
              ? "Your application was not successful this cycle. You may appeal at the CDF office."
              : appStatusVal === "SUBMITTED"
              ? "Your application has been received and is awaiting review."
              : "Your application is saved as a draft."}
          </p>
        </div>
      </div>

      {/* Rejection remarks */}
      {appStatusVal === "REJECTED" && application?.remarks && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-sm text-red-700">
          <p className="font-semibold mb-1">Reason given:</p>
          <p>{application.remarks}</p>
          <p className="text-xs text-red-400 mt-2">
            If you believe this is incorrect, visit the CDF office in Maua within 14 days to appeal.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">

        {/* Application details */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building size={16} className="text-primary" />
            <h3 className="font-semibold text-gray-800 text-sm">Application Details</h3>
          </div>
          <div className="space-y-0.5">
            <InfoRow label="Academic Year"   value={application?.academicYear} />
            <InfoRow label="Institution"     value={application?.institution?.institutionName} />
            <InfoRow label="Course / Class"  value={application?.institution?.courseOrClass} />
            <InfoRow label="Admission No."   value={application?.institution?.admissionNumber} />
            <InfoRow label="Total Fees"      value={formatKES(application?.fees?.totalFees)} />
            <InfoRow label="Fee Balance"     value={formatKES(application?.fees?.feeBalance)} />
            <InfoRow label="Submitted"       value={formatDate(application?.submittedAt)} />
          </div>
        </div>

        {/* Disbursement details */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Wallet size={16} className="text-primary" />
            <h3 className="font-semibold text-gray-800 text-sm">Disbursement Details</h3>
          </div>

          {disbStatus === "not_found" || !disbursement ? (
            <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
              <Wallet size={28} className="text-gray-200" />
              <p className="text-gray-400 text-sm">
                {appStatusVal === "APPROVED"
                  ? "Disbursement is being prepared. Check back soon."
                  : "Disbursement details will appear here once your application is approved."}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-0.5 mb-4">
                <InfoRow label="Status"         value={<StatusBadge status={disbursement.status} config={DISB_CONFIG} />} />
                <InfoRow label="Amount Awarded" value={formatKES(disbursement.amountAwarded)} />
                <InfoRow label="Payment Method" value={disbursement.paymentMethod?.replace(/_/g, " ")} />
                <InfoRow label="Reference"      value={disbursement.paymentReference || "Pending"} />
                <InfoRow label="Paid On"        value={disbursement.paidAt ? formatDate(disbursement.paidAt) : "Pending"} />
              </div>

              {/* Download letter — only when PAID */}
              {disbursement.status === "PAID" && (
                <button
                  onClick={handleDownloadLetter}
                  disabled={downloading}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-800 active:scale-95 transition disabled:opacity-60"
                >
                  {downloading
                    ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <Download size={14} />
                  }
                  Download Award Letter
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="font-semibold text-gray-800 text-sm mb-5">Application Timeline</h3>
        <div>
          {timeline.map((step, i) => (
            <TimelineStep key={step.label} {...step} last={i === timeline.length - 1} />
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        {appStatusVal === "DRAFT" && (
          <button
            onClick={() => navigate("/student-dashboard/application")}
            className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-800 transition"
          >
            Continue Application <ArrowRight size={14} />
          </button>
        )}
        <button
          onClick={() => navigate("/student-dashboard")}
          className="flex items-center gap-2 border-2 border-gray-200 text-gray-600 px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
        >
          Back to Dashboard
        </button>
      </div>

    </div>
  );
};

export default Status;
